import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthModal } from "./AuthModal";

export type AuthView = "signin" | "signup" | "forgot";

// Routes that exist only to open the modal on top of the home page
export const AUTH_ROUTES: Record<string, AuthView> = {
  "/sign-in": "signin",
  "/sign-up": "signup",
  "/forget-password": "forgot",
};

type AuthModalContextValue = {
  openAuth: (view: AuthView) => void;
  closeAuth: () => void;
};

const AuthModalContext = createContext<AuthModalContextValue | null>(null);

export const AuthModalProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [view, setView] = useState<AuthView | null>(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const openAuth = useCallback((next: AuthView) => setView(next), []);

  const closeAuth = useCallback(() => {
    setView(null);
    if (AUTH_ROUTES[pathname]) {
      navigate("/", { replace: true });
    }
  }, [navigate, pathname]);

  const value = useMemo(
    () => ({ openAuth, closeAuth }),
    [openAuth, closeAuth],
  );

  return (
    <AuthModalContext.Provider value={value}>
      {children}
      <AuthModal view={view} onSwitch={setView} onClose={closeAuth} />
    </AuthModalContext.Provider>
  );
};

export const useAuthModal = () => {
  const ctx = useContext(AuthModalContext);
  if (!ctx) {
    throw new Error("useAuthModal must be used inside AuthModalProvider");
  }
  return ctx;
};
